import { useMemo } from 'react';
import { ContractStatus } from '../types/enums';
import { CardState } from '../types/cardState';
import { GenericColors } from 'enums/genericColors';
import { useIsSendingContract } from './contractualizationStore';
import { useQuoteContractualizationData } from '../hooks/useContractualizationData';
import {
  mapApiStatusToCardState,
  mapApiSignatureToContractStatus
} from '../types/api';

// ============================================
// Selecteurs de Statut de Signature
// ============================================

/**
 * Verifie si le contrat est signe
 * Utilise contractSignatureStatus du backend
 */
export const useIsContractSigned = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  return quoteData?.contractSignatureStatus === 'Signed';
};

/**
 * Verifie si le mandat est signe
 * Utilise mandateSignatureStatus du backend
 */
export const useIsMandateSigned = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  return quoteData?.mandateSignatureStatus === 'Signed';
};

/**
 * Vrai si le devis est dans l'état "abandonné" (statusCategory "Cancelled").
 * Sert à figer l'UI : bannière + désactivation des sections d'action.
 */
export const useIsQuoteCancelled = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  return quoteData?.statusCategory === 'Cancelled';
};

/**
 * True when the validator refused the quote: the latest validation request is "Refusee".
 * The quote is then blocked for good (banner + frozen sections, like a cancelled quote).
 */
export const useIsQuoteRefused = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  return quoteData?.validation?.derniereDemande?.statut === 'Refusee';
};

/**
 * Vrai si le devis peut être abandonné : uniquement quand il s'agit encore d'un devis
 * "en cours". Masqué si le devis est déjà abandonné (statusCategory "Cancelled") ou si
 * le contrat existe déjà (contrat signé ou certificat d'adhésion émis).
 */
export const useCanAbandonQuote = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  if (!quoteData) return false;

  const isCancelled = quoteData.statusCategory === 'Cancelled';
  const hasContract =
    quoteData.contractSignatureStatus === 'Signed' || quoteData.hasCertificate;

  return !isCancelled && !hasContract;
};

// ============================================
// Selecteurs de Statut de Contrat
// ============================================

/**
 * Determine le statut du contrat
 * Utilise contractSignatureStatus du backend
 */
export const useContractStatus = (): ContractStatus => {
  const quoteData = useQuoteContractualizationData();
  return mapApiSignatureToContractStatus(quoteData?.contractSignatureStatus);
};

/**
 * Determine le CardState base sur le statut du contrat
 * Utilise cardStatuses.contract du backend
 */
export const useContractCardState = (): CardState => {
  const quoteData = useQuoteContractualizationData();
  return mapApiStatusToCardState(quoteData?.cardStatuses?.contract?.status);
};

/**
 * Determine la couleur du badge basee sur le CardState
 */
export const useContractBadgeColor = (): GenericColors => {
  const cardState = useContractCardState();

  return useMemo(() => {
    switch (cardState) {
      case CardState.CRITICAL:
        return GenericColors.Red;
      case CardState.IN_PROGRESS:
        return GenericColors.Yellow;
      case CardState.VALID:
        return GenericColors.Green;
      default:
        return GenericColors.Gray;
    }
  }, [cardState]);
};

// ============================================
// Selecteurs de Statut des Cartes
// ============================================

/**
 * Statut de la carte Conditions (SEPA/Mandate)
 * Utilise cardStatuses.conditions du backend
 */
export const useConditionCardStatus = (): CardState => {
  const quoteData = useQuoteContractualizationData();
  return mapApiStatusToCardState(quoteData?.cardStatuses?.conditions?.status);
};

/**
 * Statut de la carte Assure
 * Utilise cardStatuses.insured du backend
 */
export const useInsuredCardStatus = (): CardState => {
  const quoteData = useQuoteContractualizationData();
  return mapApiStatusToCardState(quoteData?.cardStatuses?.insured?.status);
};

/**
 * Statut de la carte Risque
 * Utilise cardStatuses.risk du backend
 */
export const useRiskCardStatus = (): CardState => {
  const quoteData = useQuoteContractualizationData();
  return mapApiStatusToCardState(quoteData?.cardStatuses?.risk?.status);
};

// ============================================
// Selecteurs de Desactivation
// ============================================

/**
 * Verifie si les cartes d'information doivent etre desactivees
 * Utilise les permissions du backend
 */
export const useIsInfoCardsDisabled = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  const isSendingContract = useIsSendingContract();

  return useMemo(() => {
    if (isSendingContract) {
      return true;
    }

    if (!quoteData?.permissions) {
      return false;
    }

    // Si aucune permission d'edition, les cartes sont desactivees
    return (
      !quoteData.permissions.canEditRisk &&
      !quoteData.permissions.canEditInsured &&
      !quoteData.permissions.canEditConditions
    );
  }, [quoteData?.permissions, isSendingContract]);
};

/**
 * Verifie si la section signature doit etre desactivee
 * Utilise permissions.canSignContract du backend
 */
export const useIsSignatureDisabled = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  const isSendingContract = useIsSendingContract();

  return useMemo(() => {
    if (isSendingContract) {
      return true;
    }
    return !quoteData?.permissions?.canSignContract;
  }, [quoteData?.permissions?.canSignContract, isSendingContract]);
};

/**
 * Verifie si le bouton d'envoi de contrat doit etre desactive
 * Utilise permissions.canSendContract du backend
 */
export const useIsSendContractDisabled = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  return !quoteData?.permissions?.canSendContract;
};

/**
 * Verifie si l'utilisateur peut signer le contrat (renvoyer la signature)
 * Utilise permissions.canSignContract du backend
 */
export const useCanSignContract = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  return quoteData?.permissions?.canSignContract === true;
};

// ============================================
// Selecteurs de Documents
// ============================================

/**
 * Verifie si le contrat est complete (signe + certificat)
 * Utilise hasCertificate + contractSignatureStatus du backend
 */
export const useIsContractCompleted = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  return (
    quoteData?.contractSignatureStatus === 'Signed' &&
    quoteData?.hasCertificate === true
  );
};

/**
 * Verifie si le certificat d'adhesion existe
 * Utilise hasCertificate du backend
 */
export const useHasCertificate = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  return quoteData?.hasCertificate === true;
};

// ============================================
// Selecteurs Utilitaires
// ============================================

/**
 * Retourne la configuration email pour l'envoi de documents
 */
export const useEmailConfig = () => {
  const quoteData = useQuoteContractualizationData();

  return useMemo(() => {
    if (!quoteData?.insured) return undefined;

    return {
      risqueAssureId: quoteData.riskId,
      sendTo: quoteData.insured.email
        ? [
            {
              email: quoteData.insured.email,
              name: quoteData.insured.displayName || undefined
            }
          ]
        : undefined
    };
  }, [quoteData?.insured, quoteData?.riskId]);
};

/**
 * Verifie si les conditions sont remplies pour fetch les documents
 */
export const useCanFetchDocuments = (): boolean => {
  const quoteData = useQuoteContractualizationData();
  const conditionStatus = useConditionCardStatus();
  const insuredStatus = useInsuredCardStatus();

  return useMemo(() => {
    return (
      conditionStatus === CardState.VALID &&
      insuredStatus === CardState.VALID &&
      quoteData != null
    );
  }, [conditionStatus, insuredStatus, quoteData]);
};
